import React, { useContext, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import Select, { IDefaultOption } from 'components/common/select';
import InputField from 'components/common/input-field';
import DatePicker from 'components/common/date-picker';
import { initSideModal, onlyNumbers, sideModalEnum, usersRoles } from 'helpers/utils';
import CheckBox from 'components/common/checkbox';
import CommonLayout from './common-layout';
import {
  IAddEditVehicleRequestBody,
  IVehicleAddEditData,
  IVehicleErrors,
  initVehicleData,
} from 'interfaces/vehicle-interface';
import { setSideModal } from 'store/features/modelSlice';
import { setAddUserModalFlag } from 'store/features/userSlice';
import {
  addVehicleAPI,
  editVehicleAPI,
  getUserListAssociateOrganization,
  setFilledVehicleDetails,
} from 'store/features/vehicleSlice';
import { getOrganizationListWithAssociateParking } from 'store/features/administratorRoleSlice';
import { AppContext } from 'context/app-context';
import {
  getBrandList,
  getDeviceProvidersList,
  getEngineList,
  getModelsList,
  getTireSizeList,
} from 'store/features/vehicleConfigSlice';
import { getCompanyList } from 'store/features/companySlice';
import { IBrand, IModel } from 'interfaces/vehicle-config-interfaces';
import toast from 'react-hot-toast';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const AddEditVehicle: React.FC = () => {
  const { setLoader } = useContext(AppContext);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  
  const {
    vehicleDetails,
    vehicleCategoryList,
    vehicleTransmissionList,
    vehicleUsedTypeList,
    vehicleStatusList,
    vehicleFuelTypeList,
    organizationAssociatedUsers,
    filledVehicleData,
    vehicleEnvLableList,
  } = useAppSelector(state => state.vehicles);
  const { isAddUserModalOpenFromUserModule } = useAppSelector(state => state.user);
  const { organizationAssociateParking } = useAppSelector(state => state.adminRole);
  const { models, brands, engines, tireSizes, deviceProviders } = useAppSelector(
    state => state.vehicleConfig,
  );
  const { companies } = useAppSelector(state => state.company);
  const { sideModal } = useAppSelector(state => state.modal);
  const { selectedOrganization } = useAppSelector(state => state.auth);

  const [vehicle, setVehicle] = useState<IVehicleAddEditData>(initVehicleData);
  // const [selectedModel, setSelectedModel] = useState<IModel | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<IBrand | null>(null);
  // const [modelOption, setModelOption] = useState<IDefaultOption[]>([]);
  const [brandOption, setBrandOption] = useState<IDefaultOption[]>([]);
  const [tireSizeOption, setTireSizeOption] = useState<IDefaultOption[]>([]);
  const [engineOption, setEngineOption] = useState<IDefaultOption[]>([]);
  const [companyOption, setCompanyOption] = useState<IDefaultOption[]>([]);
  const [deviceProviderOption, setDeviceProviderOption] = useState<IDefaultOption[]>([]);
  const [error, setError] = useState<IVehicleErrors>();
  const [dropdownSearch, serDropdownSearch] = useState<string>('');

  const { content } = sideModal;
  const { formFor, callBackFun } = content;
  const isFleetManager = selectedOrganization?.role_name === usersRoles.FLEET_MANAGE ? true : false;
  const isSuperAdmin = selectedOrganization?.role_name === usersRoles.SUPER_ADMIN ? true : false;
  const isDealerAdmin = selectedOrganization?.role_name === usersRoles.DEALER_ADMIN ? true : false;

  const callBackFuntion = () => {
    dispatch(
      setSideModal({
        type: sideModalEnum?.ADD_EDIT_VEHICLE,
        content: {
          title: formFor === 'EDIT' ? t('EDIT_VEHICLE') : t('ADD_VEHICLE'),
          subtitle: formFor === 'EDIT' ? t('EDIT_VEHICLE_SUBTITLE') : t('ADD_VEHICLE_SUBTITLE'),
          formFor: formFor,
          callBackFun: callBackFun ? () => callBackFun() : () => {},
        },
      }),
    );
  };
  const handleAddNewUser = () => {
    dispatch(setAddUserModalFlag(false));
    dispatch(setFilledVehicleDetails(vehicle || null));
    dispatch(
      setSideModal({
        type: sideModalEnum?.ADD_USER,
        content: {
          title: t('ADD_NEW_USER'),
          subtitle: t('ADD_NEW_USER_SUBTITLE'),
          callBackFun: () => callBackFuntion(),
        },
      }),
    );
  };
  const getOrganizationListAndParking = async () => {
    try {
      let params = isDealerAdmin ? { organization_id: selectedOrganization?.organization_id } : {};
      if (isDealerAdmin || isSuperAdmin) {
        await dispatch(
          getOrganizationListWithAssociateParking({
            params,
          }),
        ).catch(() => {
          setLoader(false);
        });
      } else if (isFleetManager) {
        if (vehicleDetails) {
          setVehicle({
            ...vehicle,
            organization: {
              id: vehicleDetails?.organization?._id || '',
              name: vehicleDetails?.organization?.organization_name || '',
            },
            selectedParkingAddress: vehicleDetails?.parking ? { ...vehicleDetails?.parking } : null,
          });
        }
      }
      return Promise.resolve(true);
    } catch (error) {
      setLoader(false);
      return Promise.reject(false);
    }
  };
  const getUsersList = async () => {
    try {
      setLoader(true);
      let params: { organization_id: string; company_id?: string } = {
        organization_id: vehicleDetails?.organization?._id || vehicle?.organization?.id || '',
      };
      if (selectedOrganization?.role_name === usersRoles.FLEET_MANAGE) {
        params = { ...params, company_id: selectedOrganization?.associated_company_id || '' };
      }
      await dispatch(
        getUserListAssociateOrganization({
          params: { ...params },
        }),
      ).catch(() => setLoader(false));
      setLoader(false);
    } catch (error) {
      return Promise.reject(error);
    }
  };
  useEffect(() => {
    const fetchUserList = async () => {
      try {
        await getUsersList();
      } catch (error) {
        setLoader(false);
      }
    };
    if (vehicle?.organization?.id) {
      fetchUserList();
    }
  }, [vehicle?.organization?.id]);
  const getAllOptions = async () => {
    try {
      setLoader(true);
      const promises = [
        getOrganizationListAndParking(),
        // dispatch(getModelsList({})),
        dispatch(getBrandList({})),
        dispatch(getTireSizeList({})),
        dispatch(getEngineList({})),
        dispatch(getCompanyList({ params: {}, url: '' })),
        dispatch(getDeviceProvidersList({})),
      ];
      const res = await Promise.all(promises).catch(err => {
        return Promise.reject(err);
      });
      return Promise.resolve(res);
    } catch (error) {
      return Promise.reject(error);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoader(false);
        await getAllOptions();
        setLoader(false);
      } catch (error) {
        setLoader(false);
      }
    };
    fetchData();
    if (!isAddUserModalOpenFromUserModule) {
      dispatch(setAddUserModalFlag(true));
      if (filledVehicleData) {
        setVehicle(filledVehicleData);
        dispatch(setFilledVehicleDetails(null));
      }
    }
  }, []);
  // useEffect(() => {
  //   const updatedModelOptions: IDefaultOption[] = [...models].map(data => ({
  //     id: data?._id || '',
  //     name: data?.name,
  //   }));
  //   if (!selectedModel && formFor === 'EDIT') {
  //     setSelectedModel(
  //       [...models].find(data => data?._id === vehicleDetails?.model_data?._id) || null,
  //     );
  //   }
  //   setModelOption(updatedModelOptions);
  // }, [models]);
  useEffect(() => {
    const updatedModelOptions: IDefaultOption[] = [...brands].map(data => ({
      id: data?._id || '',
      name: data?.name,
    }));
    if (!selectedBrand && formFor === 'EDIT') {
      setSelectedBrand(
        [...brands].find(data => data?._id === vehicleDetails?.brand_data?._id) || null,
      );
    }
    setBrandOption(updatedModelOptions);
  }, [brands]);
  useEffect(() => {
    const updatedOptions: IDefaultOption[] = [...tireSizes].map(data => ({
      id: data?._id || '',
      name: data?.name,
    }));
    setTireSizeOption(updatedOptions);
  }, [tireSizes]);
  useEffect(() => {
    const updatedOptions: IDefaultOption[] = [...engines].map(data => ({
      id: data?._id || '',
      name: data?.name,
    }));
    setEngineOption(updatedOptions);
  }, [engines]);
  useEffect(() => {
    const updatedOptions: IDefaultOption[] = [...companies].map(data => ({
      id: data?._id || '',
      name: data?.name,
    }));
    setCompanyOption(updatedOptions);
  }, [companies]);
  useEffect(() => {
    const updatedOptions: IDefaultOption[] = [...deviceProviders].map(data => ({
      id: data?._id || '',
      name: data?.name,
    }));
    setDeviceProviderOption(updatedOptions);
  }, [deviceProviders]);
  useEffect(() => {
    if (formFor === 'EDIT') {
      const vehicleFormatedData: IVehicleAddEditData = {
        _id: vehicleDetails?._id || '',
        // model: {
        //   id: vehicleDetails?.model?. || '',
        //   name: vehicleDetails?.model_data?.name || '',
        // },
        model: vehicleDetails?.model || '',
        brand: {
          id: vehicleDetails?.brand_data?._id || '',
          name: vehicleDetails?.brand_data?.name || '',
        },
        registration_date: vehicleDetails?.registration_date || '',
        purchase_date: vehicleDetails?.purchase_date || '',
        organization: {
          id: vehicleDetails?.organization?._id || '',
          name: vehicleDetails?.organization?.organization_name || '',
        },
        selectedParkingAddress: vehicleDetails?.parking || null,
        category:
          vehicleCategoryList.find(
            data =>
              data.id === vehicleDetails?.vehicle_data?.category ||
              data.value === vehicleDetails?.vehicle_data?.category,
          ) || null,
        plate: vehicleDetails?.plate || '',
        chasis: vehicleDetails?.chasis || '',
        km: vehicleDetails?.vehicle_data?.km ? String(vehicleDetails?.vehicle_data?.km) : '',
        engine: vehicleDetails?.engine_data._id
          ? {
              id: vehicleDetails?.engine_data._id || '',
              name: vehicleDetails?.engine_data.name || '',
            }
          : null,
        front_tire_size: vehicleDetails?.front_tire_size_data?._id
          ? {
              id: vehicleDetails?.front_tire_size_data?._id || '',
              name: vehicleDetails?.front_tire_size_data?.name || '',
            }
          : null,
        rear_tire_size: vehicleDetails?.rear_tire_size_data?._id
          ? {
              id: vehicleDetails?.rear_tire_size_data?._id || '',
              name: vehicleDetails?.rear_tire_size_data?.name || '',
            }
          : null,
        fuel_type:
          vehicleFuelTypeList.find(
            data =>
              data.id === vehicleDetails?.vehicle_data?.fuel_type ||
              data.value === vehicleDetails?.vehicle_data?.fuel_type,
          ) || null,
        transmission:
          vehicleTransmissionList.find(
            data =>
              data.id === vehicleDetails?.vehicle_data?.transmission ||
              data.value === vehicleDetails?.vehicle_data?.transmission,
          ) || null,
        doors: vehicleDetails?.vehicle_data?.doors,
        places: vehicleDetails?.vehicle_data?.places,
        is_used: String(vehicleDetails?.vehicle_data?.is_used),
        status:
          vehicleStatusList.find(
            data => data.id === vehicleDetails?.status || data.value === vehicleDetails?.status,
          ) || null,
        assignedUser: vehicleDetails?.user?._id
          ? {
              id: vehicleDetails?.user?._id || '',
              name: `${vehicleDetails?.user?.name} (${vehicleDetails?.user?.email})` || '',
            }
          : null,
        associtedCompany: vehicleDetails?.associatedCompany?._id
          ? {
              id: vehicleDetails?.associatedCompany?._id || '',
              name: vehicleDetails?.associatedCompany?.name || '',
            }
          : null,
        environmental_label:
          vehicleDetails?.vehicle_data?.environmental_label === 'NONE'
            ? null
            : vehicleEnvLableList.find(
                data => data.id === vehicleDetails?.vehicle_data?.environmental_label,
              ) || null,
        device: vehicleDetails?.device_details?.imeie || '',
        version: vehicleDetails?.vehicle_data?.version || '',
        provider: vehicleDetails?.provider_details
          ? {
              id: vehicleDetails?.provider_details?._id || '',
              name: vehicleDetails?.provider_details?.name || '',
            }
          : null,
      };
      setVehicle(vehicleFormatedData);
    }
  }, []);
  const getParkingList = () => {
    try {
      const selectedOrganization = organizationAssociateParking.find(
        data => data._id === vehicle?.organization?.id,
      );
      return selectedOrganization?.parking
        ? [...selectedOrganization?.parking.filter(item => !item?.is_deleted)]
        : [];
    } catch (error) {
      return [];
    }
  };
  const getErrorData = () => {
    return {
      // model: !vehicle?.model?.id ? t('PLEASE_SELECT_MODEL') : '',
      model: vehicle?.model === '' ? t('PLEASE_ENTER_MODEL') : '',
      brand: !vehicle?.brand?.id ? t('PLEASE_SELECT_BRAND') : '',
      registration_date: !vehicle?.registration_date?.trim() ? t('PLEASE_SELECT_REG_DATE') : '',
      organization: !vehicle.organization?.id ? t('PLEASE_SELECT_ORG') : '',
      plate: !vehicle.plate?.trim() ? t('PLEASE_SELECT_PLATE') : '',
      chasis: !vehicle.chasis?.trim() ? t('PLEASE_ENTER_VIN') : '',
      km: !vehicle?.km?.trim() ? t('PLEASE_ENTER_KM') : '',
      engine_type: !vehicle?.engine?.id ? t('PLEASE_ENTER_ENGINE') : '',
      transmission: !vehicle?.transmission?.id ? t('PLEASE_SELECT_TRANSMISSION') : '',
      device:
        vehicle?.provider?.id && !vehicle?.device?.trim() ? t('PLEASE_ENTER_DEVICE_IMEI') : '',
      provider:
        !vehicle?.provider?.id && vehicle?.device?.trim() ? t('PLEASE_SELECT_DEVICE_PROVIDER') : '',
      status: !vehicle.status?.id ? t('PLEASE_SELECT_STATUS') : '',
      is_used: vehicle?.is_used === undefined ? 'Is used/ is new is not selected' : '',
      // purchase_date: !vehicle?.purchase_date?.trim() ? t('PLEASE_SELECT_PUR_DATE') : '',
      // category: !vehicle?.category?.id ? t('PLEASE_SELECT_CAT') : '',
      // tuition: !vehicle?.tutaion?.trim() ? t('PLEASE_ENTER_TUTION') : '',
      // frame: !vehicle?.frame?.trim() ? t('PLEASE_ENTER_FRAME') : '',
      // fuel_type: !vehicle?.fuel_type?.id ? t('PLEASE_SELECT_FUELTYPE') : '',
    };
  };
  const isValid = () => {
    if (
      !vehicle?.registration_date?.trim() ||
      !vehicle?.organization?.id ||
      // !vehicle?.model?.id ||
      vehicle?.model === '' ||
      !vehicle?.brand?.id ||
      !vehicle?.plate?.trim() ||
      !vehicle?.chasis?.trim() ||
      !vehicle?.engine?.id ||
      !vehicle?.transmission?.id ||
      !vehicle?.status?.id ||
      !vehicle?.km?.trim() ||
      (vehicle?.device && !vehicle?.provider?.id) ||
      (!vehicle?.device && vehicle?.provider?.id) ||
      vehicle?.is_used === undefined
    ) {
      const output = getErrorData();
      setError(output);
      return false;
    } else {
      const output = getErrorData();
      setError(output);
      return true;
    }
  };
  const getRequestParams = () => {
    let request: IAddEditVehicleRequestBody = {
      organization_id: vehicle?.organization?.id || '',
      brand_id: vehicle?.brand?.id || '',
      model: vehicle?.model || '',
      registration_date: moment(vehicle?.registration_date).format('DD/MM/YYYY'),
      status: vehicle?.status?.name || '',
      plate: vehicle?.plate,
      chasis: vehicle?.chasis,
      km: Number(vehicle?.km) || 0,
      engine_id: vehicle?.engine?.id || '',
      parking_id: vehicle?.selectedParkingAddress?._id || '',
      environmental_label: vehicle?.environmental_label?.value || 'NONE',
      transmission: vehicle?.transmission?.value || '',
      is_used: vehicle?.is_used === 'false' ? false : true,
    };

    // Only add tire size IDs if they have values
    if (vehicle?.front_tire_size?.id) {
      request.front_tire_size_id = vehicle.front_tire_size.id;
    }
    if (vehicle?.rear_tire_size?.id) {
      request.rear_tire_size_id = vehicle.rear_tire_size.id;
    }

    if (vehicle?.purchase_date) {
      request.purchase_date = moment(vehicle?.purchase_date).format('DD/MM/YYYY');
    }
    if (vehicle?.assignedUser?.id) {
      request.user_id = vehicle?.assignedUser?.id;
    }
    if (vehicle?.associtedCompany?.id) {
      request.associated_company = vehicle?.associtedCompany?.id;
    }
    if (vehicle?.category?.value) {
      request.category = vehicle?.category?.value;
    }
    if (vehicle?.fuel_type?.value) {
      request.fuel_type = vehicle?.fuel_type?.name;
    }
    if (vehicle.version) {
      request.version = vehicle?.version;
    }
    if (vehicle?.doors) {
      request.doors = vehicle?.doors;
    }
    if (vehicle?.places) {
      request.places = vehicle?.places;
    }
    if (vehicle?.provider?.id && vehicle?.device?.trim()) {
      request.device_detail = {
        imeie: vehicle?.device,
        provider_id: vehicle?.provider?.id || vehicle?.provider?._id || '',
      };
    }
    return request;
  };

  const handleAddVehicle = () => {
    try {
      setLoader(true);
      let request: IAddEditVehicleRequestBody = getRequestParams();
      dispatch(addVehicleAPI({ request: { ...request } }))
        .then(() => {
          toast.success(t('VEHICLE_ADD_SUCCESS_MSG'));
          callBackFun && callBackFun();
          dispatch(setSideModal(initSideModal));
          setLoader(false);
        })
        .catch(() => setLoader(false));
    } catch (error) {
      setLoader(false);
    }
  };
  const handleEditVehicle = () => {
    try {
      setLoader(true);
      let request = getRequestParams();
      dispatch(editVehicleAPI({ request: { ...request }, vehicle_id: vehicle?._id }))
        .then(() => {
          toast.success(t('VEHCILE_UPDATE_SUCCESS_MESSAGE'));
          callBackFun && callBackFun();
          dispatch(setSideModal(initSideModal));
          setLoader(false);
        })
        .catch(() => setLoader(false));
    } catch (error) {
      setLoader(false);
    }
  };
  const handleSubmit = () => {
    try {
      if (!isValid()) {
        return;
      }
      if (formFor === 'EDIT') {
        handleEditVehicle();
      } else {
        handleAddVehicle();
      }
    } catch (error) {
      setLoader(false);
    }
  };

  return (
    <CommonLayout
      onConfirm={() => handleSubmit()}
      confirmButtonProps={{ label: formFor === 'EDIT' ? t('SAVE_CHANGES') : t('ADD_VEHICLE') }}
    >
      <div className="flex flex-wrap p-6">
        <div className="flex items-center justify-center w-1/2 px-3 pb-4">
          <div className="w-[60%] h-full flex items-center justify-center rounded-3 bg-grey4">
            {!selectedBrand?.image_url ? (
              <label className="text-lg font-semibold w-full h-full flex items-center justify-center">
                {t('NO_IMG')}
              </label>
            ) : (
              <img
                src={selectedBrand?.image_url}
                alt="Org-LOGO"
                className="h-full w-full rounded-3 object-contain"
              />
            )}
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('BRAND')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              placeholder={t('PLEASE_SELECT_BRAND')}
              selected={vehicle?.brand || null}
              setSelected={val => {
                setVehicle({ ...vehicle, brand: val });
                const selBrand = brands.find(data => data._id === val.id) || null;
                setSelectedBrand(selBrand);
              }}
              options={brandOption}
              error={error?.brand || ''}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({ ...vehicle, brand: null });
                setSelectedBrand(null);
              }}
            />
            {/* <InputField
              placeholder={t('BRAND')}
              value={selectedModel?.brandDetails?.name || ''}
              onChange={val => {}}
              disabled={true}
            /> */}
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('MODEL')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            {/* <Select
              placeholder={t('SELECT_MODEL')}
              selected={vehicle?.model || null}
              setSelected={val => {
                setVehicle({ ...vehicle, model: val });
                const selModel = models.find(data => data._id === val.id) || null;
                setSelectedModel(selModel);
              }}
              options={modelOption}
              error={error?.model || ''}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({ ...vehicle, model: null });
                setSelectedModel(null);
              }}
            /> */}

            <InputField
              placeholder={t('MODEL')}
              value={vehicle?.model || ''}
              onChange={val => setVehicle({ ...vehicle, model: val })}
              error={error?.model || ''}
              disabled={isFleetManager}
              // disabled={true}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('REG_DATE')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <DatePicker
              selected={vehicle?.registration_date}
              onChange={date => setVehicle({ ...vehicle, registration_date: String(date) || '' })}
              error={error?.registration_date || ''}
              disabled={isFleetManager}
              isAllowFutureDate={false}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('PUR_DATE')}</label>
            <DatePicker
              selected={vehicle?.purchase_date}
              onChange={date => setVehicle({ ...vehicle, purchase_date: String(date) || '' })}
              disabled={isFleetManager}
              isAllowFutureDate={false}
            />
          </div>
        </div>
        <div className="w-full px-3">
          <label className="font-bold text-base text-black2">{t('ORG_DETAILS')}</label>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('ORG')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              options={organizationAssociateParking}
              selected={vehicle?.organization || null}
              setSelected={val => setVehicle({ ...vehicle, organization: { ...val } })}
              placeholder={t('SELE_ORG')}
              error={error?.organization || ''}
              isSearchable
              search={dropdownSearch}
              setSearch={val => serDropdownSearch(val)}
              removeSearchOnBlur={true}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({ ...vehicle, organization: null });
              }}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('ASSIGN_USR')}</label>
            <Select
              placeholder={t('SELECT_ASSIGNED_USR')}
              selected={vehicle?.assignedUser}
              setSelected={val => setVehicle({ ...vehicle, assignedUser: val })}
              options={organizationAssociatedUsers || []}
              isSearchable
              search={dropdownSearch}
              setSearch={val => serDropdownSearch(val)}
              removeSearchOnBlur={true}
              showCross
              handleCross={() => setVehicle({ ...vehicle, assignedUser: null })}
            />
            {(isSuperAdmin || isDealerAdmin) && vehicle?.organization ? (
              <div className="flex items-center justify-end gap-x-1 mt-1 mr-2 w-full">
                <label
                  className="text-sm font-medium text-primary underline cursor-pointer w-fit text-right"
                  onClick={handleAddNewUser}
                >
                  {t('ADD_NEW_USER')}
                </label>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className="w-full px-3">
          <div className="flex flex-wrap rounded-2 mt-3 bg-bg-50 p-4 mb-3 border-1.5">
            {isFleetManager ? (
              <div className="flex flex-col w-1/3 p-3" key={vehicle?.selectedParkingAddress?._id}>
                <div className="relative ps-6" key={vehicle?.selectedParkingAddress?._id}>
                  <div className="absolute left-0 top-[-3px] mt-[6px]">
                    <CheckBox checked={true} onChange={checked => {}} disabled={isFleetManager} />
                  </div>
                  <label className="block text-base font-bold w-full">{t('ADDRESS_1')}</label>
                  <label className="text-sm text-black2 w-full flex flex-wrap break-words">
                    {`${vehicle?.selectedParkingAddress?.address1}, ${
                      vehicle?.selectedParkingAddress?.address2
                        ? `${vehicle?.selectedParkingAddress?.address2},`
                        : ''
                    } ${vehicle?.selectedParkingAddress?.city}, ${vehicle?.selectedParkingAddress
                      ?.state}, ${vehicle?.selectedParkingAddress?.country} - ${vehicle
                      ?.selectedParkingAddress?.zipcode}.`}
                  </label>
                </div>
              </div>
            ) : getParkingList().length > 0 ? (
              getParkingList().map((parking_add, index) => (
                <div className="flex flex-col w-1/3 p-3" key={parking_add?._id}>
                  <div className="relative ps-6" key={parking_add?._id}>
                    <div className="absolute left-0 top-[-3px] mt-[6px]">
                      <CheckBox
                        checked={parking_add?._id === vehicle?.selectedParkingAddress?._id}
                        onChange={checked =>
                          setVehicle({
                            ...vehicle,
                            selectedParkingAddress: checked
                              ? {
                                  address1: parking_add?.address1 || '',
                                  address2: parking_add?.address2 || '',
                                  city: parking_add?.city,
                                  state: parking_add?.state,
                                  country: parking_add?.country,
                                  zipcode: parking_add?.zipcode,
                                  _id: parking_add?._id || '',
                                }
                              : null,
                          })
                        }
                        disabled={isFleetManager}
                      />
                    </div>
                    <label className="block text-base font-bold w-full">
                      {parking_add?.address_title || `Address ${index + 1}`}
                    </label>
                    <label className="text-sm text-black2 w-full flex flex-wrap break-words">
                      {`${parking_add?.address1}, ${
                        parking_add?.address2 ? `${parking_add?.address2},` : ''
                      } ${parking_add?.city}, ${parking_add?.state}, ${parking_add?.country} - ${parking_add?.zipcode}.`}
                    </label>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center py-4 px-1 w-full rounded-2 bg-bg-50">
                <label className="text-base font-medium text-grey3 text-center w-full mx-auto">
                  {t('EMPTY_PARK_ADD_MSG')}
                </label>
              </div>
            )}
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('ASSOCIATED_COMPANY')}
            </label>
            <Select
              options={companyOption}
              selected={vehicle?.associtedCompany || null}
              placeholder={t('PLEASE_SEL_ASS_COM')}
              setSelected={data => setVehicle({ ...vehicle, associtedCompany: data })}
              showCross
              handleCross={() => setVehicle({ ...vehicle, associtedCompany: null })}
              disabled={isFleetManager}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('CAT_ORG')}</label>
            <Select
              placeholder={t('SEL_CAT_ORG')}
              selected={vehicle?.category || null}
              setSelected={val => setVehicle({ ...vehicle, category: val })}
              options={vehicleCategoryList}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({ ...vehicle, category: null });
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('PLATE')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <InputField
              placeholder={t('ENTER_PLATE')}
              value={vehicle?.plate || ''}
              onChange={val => setVehicle({ ...vehicle, plate: val })}
              error={error?.plate || ''}
              disabled={isFleetManager}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('VIN')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <InputField
              placeholder={t('ENTER_VIN')}
              value={vehicle?.chasis || ''}
              onChange={val => setVehicle({ ...vehicle, chasis: val })}
              error={error?.chasis || ''}
              disabled={isFleetManager}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('KM')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <InputField
              placeholder={t('ENTER_KM')}
              value={
                typeof vehicle?.km === 'undefined' || vehicle.km === '' ? '' : vehicle?.km || ''
              }
              onChange={val =>
                setVehicle({
                  ...vehicle,
                  km: onlyNumbers.test(val) || !val.trim() ? val : vehicle?.km,
                })
              }
              disabled={isFleetManager}
              error={error?.km}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('ENGINE_TYPE')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              placeholder={t('SEL_ENGINE')}
              selected={vehicle?.engine || null}
              setSelected={val => setVehicle({ ...vehicle, engine: val })}
              options={engineOption}
              error={error?.engine_type || ''}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({ ...vehicle, engine: null });
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('TRANSMISSION')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              placeholder={t('TRANSMISSION')}
              selected={vehicle?.transmission || null}
              setSelected={val => setVehicle({ ...vehicle, transmission: val })}
              options={vehicleTransmissionList}
              error={error?.transmission || ''}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({ ...vehicle, transmission: null });
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('NEW_CAR_OR_USED_CAR')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              placeholder={t('NEW_CAR_OR_USED_CAR')}
              selected={
                vehicle?.is_used === undefined
                  ? null
                  : vehicle.is_used === 'true'
                  ? { id: 'USED_CAR', name: 'Used Car', value: 'true' }
                  : { id: 'NEW_CAR', name: 'New Car', value: 'false' }
              }
              setSelected={val => setVehicle({ ...vehicle, is_used: val.value })}
              options={vehicleUsedTypeList}
              dropdownPlacement="BOTTOM"
              disabled={isFleetManager}
              error={error?.is_used}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('FRONT_TIRE_SIZE')}
            </label>
            <Select
              placeholder={t('SEL_TIRE_SIZE')}
              selected={vehicle?.front_tire_size || null}
              setSelected={val => setVehicle({ ...vehicle, front_tire_size: val })}
              options={tireSizeOption}
              error={error?.front_tire_size || ''}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({ ...vehicle, front_tire_size: null });
              }}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('REAR_TIRE_SIZE')}
            </label>
            <Select
              placeholder={t('SEL_REAR_TIRE_SIZE')}
              selected={vehicle?.rear_tire_size || null}
              setSelected={val => setVehicle({ ...vehicle, rear_tire_size: val })}
              options={tireSizeOption}
              error={error?.rear_tire_size || ''}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({ ...vehicle, rear_tire_size: null });
              }}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('DEVICE_PROVIDER')}</label>
            <Select
              placeholder={t('SEL_DEVICE_PROVIDER')}
              selected={vehicle?.provider || null}
              setSelected={val => setVehicle({ ...vehicle, provider: val })}
              options={deviceProviderOption}
              error={error?.provider || ''}
              disabled={isFleetManager}
              handleCross={() => setVehicle({ ...vehicle, provider: null })}
              showCross
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('Device_IMEI')}</label>
            <InputField
              placeholder={t('ENTER_DEVICE_IMEI')}
              value={vehicle?.device || ''}
              onChange={val => setVehicle({ ...vehicle, device: val })}
              error={error?.device || ''}
              disabled={isFleetManager || !vehicle?.provider}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('FUEL_TYPE')}</label>
            <Select
              placeholder={t('FUEL_TYPE')}
              selected={vehicle?.fuel_type || null}
              setSelected={val => setVehicle({ ...vehicle, fuel_type: val })}
              options={vehicleFuelTypeList}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({ ...vehicle, fuel_type: null });
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('VERSION')}</label>
            <InputField
              placeholder={t('ENTER_VERSION')}
              value={vehicle?.version || ''}
              onChange={val => setVehicle({ ...vehicle, version: val })}
              error={''}
              disabled={isFleetManager}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('DOORS')}</label>
            <InputField
              placeholder={t('ENTER_DOORS')}
              value={vehicle?.doors || ''}
              onChange={val => setVehicle({ ...vehicle, doors: val })}
              disabled={isFleetManager}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('PLACES')}</label>
            <InputField
              placeholder={t('ENTER_PLACES')}
              value={vehicle?.places || ''}
              onChange={val => setVehicle({ ...vehicle, places: val })}
              disabled={isFleetManager}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('ENV_LABLE')}</label>
            <Select
              placeholder={t('SEL_ENV_LABEL')}
              selected={vehicle?.environmental_label || null}
              setSelected={val => setVehicle({ ...vehicle, environmental_label: val })}
              options={vehicleEnvLableList}
              dropdownPlacement={'TOP'}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({
                  ...vehicle,
                  environmental_label: null,
                });
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">{t('STATUS')}</label>
            <Select
              placeholder={t('PLEASE_SELECT_STATUS')}
              selected={vehicle?.status || null}
              setSelected={val => setVehicle({ ...vehicle, status: val })}
              options={vehicleStatusList}
              dropdownPlacement={'TOP'}
              error={error?.status || ''}
              disabled={isFleetManager}
              showCross
              handleCross={() => {
                setVehicle({
                  ...vehicle,
                  status: null,
                });
              }}
            />
          </div>
        </div>
        {/* <div className="w-full px-3">
          <div className="flex items-center mb-3 gap-x-2">
            <div className="flex items-center justify-center">
              <CheckBox
                checked={vehicle?.environmental_label}
                onChange={checked =>
                  setVehicle({
                    ...vehicle,
                    environmental_label: checked,
                  })
                }
                disabled={selectedOrganization?.role_name === usersRoles.FLEET_MANAGE}
              />
            </div>
            <label className="text-base font-medium flex-1">Environmental Label</label>
          </div>
        </div> */}
      </div>
    </CommonLayout>
  );
};

export default AddEditVehicle;
